/**
 * @file edge_machine_app_interface.js
 * @author MachineApps Team
 *
 * @description Interface provided by the edge to the calibrator and generator.
 */
const immediateApi = require('../api/immediateApi').default;
const base_machine_app_interface = require('./base_machine_app_interface');
const utils = require('../utils');
const { stopAll, getIosFromUuids } = require('./machineapp_utils');
const { jogger } = require("./components/machine_app_jogger");
const { getRobotJoggerModal, cartesianPoseTo3DTransformationMatrix } = require("./components/machine_app_robot_jogger");
const { controlCenter } = require("@ventionco/mm-machine-logic-robot");

const { api: RobotJoggerAPI } = controlCenter;

const M_to_MM = 1000;

function EdgeMachineAppInterface(pNavigate, pMachineLogicLibrary, pMachineAppLibraryMgr) {
  base_machine_app_interface.BaseMachineAppInterface.call(this, pMachineLogicLibrary, pMachineAppLibraryMgr);

  this.navigate = pNavigate;
}

EdgeMachineAppInterface.prototype = Object.create(base_machine_app_interface.BaseMachineAppInterface.prototype);


/*********************************************************************************
  * METHODS REQUIRED PER BASE INTERFACE
  **********************************************************************************/
EdgeMachineAppInterface.prototype.isProdDeployment = function() {
  return false;
};

EdgeMachineAppInterface.prototype.startRobotJogger = function(pRobotId, { onStop = undefined, onTeach = undefined, closeButtonLabel = undefined } = {}) {
  const lRobotType = this.getRobot(pRobotId).getType();

  getRobotJoggerModal(this.mMachineLogicLibrary, lRobotType, { onClose: onStop, onTeach, closeButtonLabel });
};

EdgeMachineAppInterface.prototype.getRobotPosition = async function() {
  const { cartesianPose, jointStates } = await RobotJoggerAPI.fetchRobotState();
  const [x, y, z, qx, qy, qz, qw] = cartesianPose; // (x, y, z) are in meters

  return {
    pose: cartesianPoseTo3DTransformationMatrix([x * M_to_MM, y * M_to_MM, z * M_to_MM, qx, qy, qz, qw]),
    jointAngles: jointStates, // [j1, j2, j3, j4, j5, j6, ...]
  };
};

EdgeMachineAppInterface.prototype.getRobotReferenceFrameWorld = function(pCalibrationData){
  // This linear matrix format is robot team's
  const lIdentityMatrix = [
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
  ];

  return lIdentityMatrix;
};

EdgeMachineAppInterface.prototype.cleanupCalibratorManipulators = function() {
  //DO NOTHING
};

EdgeMachineAppInterface.prototype.graphHasAxis = function(pVid) {
  //this must return true to circumvent the validation check of a part existing in the graph (a Cloud specific validation)
  return true;
};

EdgeMachineAppInterface.prototype.getCartesianJogger = function({ axisMapping, options, onPosition, instructionText }) {
  return jogger(this._getMachineLogicLibrary(), axisMapping, options, onPosition, instructionText);
};

/*
  * Update / Redirect views NOT USED IN MACHINE-APP-COMPONENTS REPO
  */
EdgeMachineAppInterface.prototype.updateMachineAppView = function(pAppType, pAppView) {
  let search = "";
  if (pAppType) {
    search = `?type=${pAppType}&view=${pAppView || 'configuration'}`;
  }

  // TODO GD We must test this, it probably doesn't work anymore
  // The pendant is now using react, the navigate callback will try to re-render the page because we're likely already on it
  // Since the page is managed by jQuery, react won't re-render anything because no state has changed
  // TODO GD We translated 1:1 from '/machine-app.html' to '/machine-app', but 'machine-app.html' didn't even exist??
  // TODO GD This code should ultimately live in the pendant. The VSE should not be aware of the pendant routes
  this.navigate({ pathname: "/machine-app", search }, { replace: true });
};

EdgeMachineAppInterface.prototype.redirectMachineAppToConfigure = function() {
  // TODO GD This code should ultimately live in the pendant. The VSE should not be aware of the pendant routes
  this.navigate("/machine-config");
};

EdgeMachineAppInterface.prototype.redirectMachineAppToMachineLogic = function (machineAppName) {
  // TODO GD This code should ultimately live in the pendant. The VSE should not be aware of the pendant routes
  this.navigate("/machine-logic");
};

EdgeMachineAppInterface.prototype.redirectMachineAppToAppLauncher = function (pAppName) {
  if (!pAppName) {
    console.error('You must specify a pAppName to redirect to the app launcher');
    return;
  }

  // TODO GD This code should ultimately live in the pendant. The VSE should not be aware of the pendant routes
  this.navigate({ pathname: "/appLauncher", search: `?app=${pAppName}` });
};

EdgeMachineAppInterface.prototype.redirectToMachineAppView = function(pAppType, pAppView) {
  let search = "";
  if (pAppType) {
    search = `?type=${pAppType}&view=${pAppView || 'configuration'}`;
  }

  // TODO GD We translated 1:1 from '/machine-app.html' to '/machine-app', but 'machine-app.html' didn't even exist??
  // TODO GD This code should ultimately live in the pendant. The VSE should not be aware of the pendant routes
  this.navigate({ pathname: "/machine-app", search });
};


/*********************************************************************************
  * EDGE SPECIFIC INTERFACES
  **********************************************************************************/
EdgeMachineAppInterface.prototype.setOutput = function(pUuid, pValue, pCallback) {
  const lOutput = this.mMachineLogicLibrary.getConfigManager().getOutputByUuid(pUuid);
  if (!lOutput) {
    console.error('Unable to find output with uuid=' + pUuid);
    if (pCallback) pCallback(false);
    return;
  }

  immediateApi.writeOutput(pUuid, pValue).then(function(pResult) {
    if (pCallback) pCallback(pResult.success || false);
  });
};

/**
   * Asynchronously get the current state of an output device.
   * @param {string} pOutputUuid - The uuid of the output device to get the state of.
   * @param {(pStatus: (int | undefined)) => void} pOutputStateHandler - A function that takes the output state (int) as parameter. If an error occurred, this value will be undefined.
   * @returns {void} - Nothing. You handle the output state via the pOutputStateHandler.
   */
EdgeMachineAppInterface.prototype.getOutputState = function(pOutputUuid, pOutputStateHandler) {
  const lOutput = this.mMachineLogicLibrary.getConfigManager().getOutputByUuid(pOutputUuid);
  if (!lOutput) {
    console.error(`Unable to find output with uuid=${pOutputUuid}`);
    if (pOutputStateHandler) {
      pOutputStateHandler(undefined);
    }

    return;
  }

  immediateApi.getIoState(lOutput.getControllerId()).then(function (pOutputState) {
    const lStatus = pOutputState.status.find(function(pStatus) {
      return pStatus.device === lOutput.getDevice();
    });

    if (lStatus && lStatus.outputs) {
      pOutputStateHandler(lStatus.outputs[lOutput.getPin()]);
    }
    else {
      pOutputStateHandler(undefined);
    }
  });
};

/**
   * Asynchronously get the current state of multiple output devices.
   * @param {string[]} pOutputUuids - A list of uuids of the output devices to get the state of.
   * @param {(pStatus: (Object.<outputUuid: string, outputState: int>)) => void} pOutputStatesHandler - A function that takes an output states map as parameter. The key is the output uuid, the value is its state. If a key is missing, it means it could not be retrieved.
   * @returns {void} - Nothing. You handle the output states via the pOutputStatesHandler.
   */
EdgeMachineAppInterface.prototype.getOutputStateList = function(pOutputUuids, pOutputStatesHandler) {
  const lOutputs = getIosFromUuids(pOutputUuids, this.mMachineLogicLibrary.getConfigManager().listOutputs());
  const lControllerIds = utils.getUniqueValues(lOutputs.map((lOutput) => lOutput.controllerId));
  const lOutputStatePromises = lControllerIds.map(
    lControllerId => immediateApi
      .getIoState(lControllerId)
      .then(pControllerDeviceStates => pControllerDeviceStates.status.map((deviceState) => ({ controllerId: lControllerId, device: deviceState.device, outputs: deviceState.outputs })))
  );
  Promise.all(lOutputStatePromises).then(pOutputStates => {
    const lOutputStatesMap = {};
    for (const pIoState of pOutputStates.flat()) {
      if(!lOutputStatesMap[pIoState.controllerId]) {
        lOutputStatesMap[pIoState.controllerId] = {};
      }

      lOutputStatesMap[pIoState.controllerId][pIoState.device] = pIoState.outputs;
    }

    const lRequestedOutputStates = {};
    for (const lOutput of lOutputs) {
      if (lOutputStatesMap[lOutput.controllerId] &&
            lOutputStatesMap[lOutput.controllerId][lOutput.device]) {
        lRequestedOutputStates[lOutput.uuid] = lOutputStatesMap[lOutput.controllerId][lOutput.device][lOutput.pinNumber];
      }
    }

    pOutputStatesHandler(lRequestedOutputStates);
  });
};

/**
   * Asynchronously get the current state of an input device.
   * @param {string} pInputUuid - The uuid of the input device to get the state of.
   * @param {(pStatus: (int | undefined)) => void} pInputStateHandler - A function that takes the output state (int) as parameter. If an error occurred, this value will be undefined.
   * @returns {void} - Nothing. You handle the output state via the pInputStateHandler.
   */
EdgeMachineAppInterface.prototype.getInputState = function(pInputUuid, pInputStateHandler) {
  const lInput = this.mMachineLogicLibrary.getConfigManager().getInputByUuid(pInputUuid);
  if (!lInput) {
    console.error('Unable to find input with uuid=' + pInputUuid);
    if (pInputStateHandler) pInputStateHandler(undefined);
    return;
  }

  immediateApi.getIoState(lInput.getControllerId()).then(function(pInputState) {
    const lStatus = pInputState.status.find(function(pStatus) {
      return pStatus.device === lInput.getDevice();
    });

    if (lStatus && lStatus.pinStates) {
      pInputStateHandler(lStatus.pinStates[lInput.getPin()]);
    }
    else {
      pInputStateHandler(undefined);
    }
  });
};

/**
   * Asynchronously get the current state of multiple input devices.
   * @param {string[]} pInputUuids - A list of uuids of the input devices to get the state of.
   * @param {(pStatus: (Object.<inputUuid: string, inputState: int>)) => void} pInputStatesHandler - A function that takes an input states map as parameter. The key is the input uuid, the value is its state. If a key is missing, it means it could not be retrieved.
   * @returns {void} - Nothing. You handle the input states via the pInputStatesHandler.
   */
EdgeMachineAppInterface.prototype.getInputStateList = function(pInputUuids, pInputStatesHandler) {
  const lInputs = getIosFromUuids(pInputUuids, this.mMachineLogicLibrary.getConfigManager().listInputs());
  const lControllerIds = utils.getUniqueValues(lInputs.map((lInput) => lInput.controllerId));
  const lInputStatePromises = lControllerIds.map(
    lControllerId => immediateApi
      .getIoState(lControllerId)
      .then(pControllerDeviceStates => pControllerDeviceStates.status.map((deviceState) => ({ controllerId: lControllerId, device: deviceState.device, inputs: deviceState.pinStates })))
  );
  Promise.all(lInputStatePromises).then(pInputStates => {
    const lInputStatesMap = {};
    for (const pIoState of pInputStates.flat()) {
      if(!lInputStatesMap[pIoState.controllerId]) {
        lInputStatesMap[pIoState.controllerId] = {};
      }

      lInputStatesMap[pIoState.controllerId][pIoState.device] = pIoState.inputs;
    }

    const lRequestedInputStates = {};
    for (const lInput of lInputs) {
      if (lInputStatesMap[lInput.controllerId] &&
            lInputStatesMap[lInput.controllerId][lInput.device]) {
        lRequestedInputStates[lInput.uuid] = lInputStatesMap[lInput.controllerId][lInput.device][lInput.pinNumber];
      }
    }

    pInputStatesHandler(lRequestedInputStates);
  });
};

EdgeMachineAppInterface.prototype.homeAxis = function(pUuid, pCallback) {
  const lAxis = this._getMachineLogicLibrary().getConfigManager().getAxisByUuid(pUuid);
  if (!lAxis) {
    console.error('Unable to jog the axis with uuid=' + pUuid);
    if (pCallback) pCallback(false);
    return;
  }

  immediateApi.home(pUuid).then(function(pResult) {
    if (pCallback) pCallback(Boolean(pResult));
  });
};

EdgeMachineAppInterface.prototype.jogAxis = function(pUuid, pSpeed, pAcceleration, pJogIncrement, pCallback) {
  const lAxis = this._getMachineLogicLibrary().getConfigManager().getAxisByUuid(pUuid);
  if (!lAxis) {
    console.error('Unable to jog the axis with uuid=' + pUuid);
    if (pCallback) pCallback(false);
    return;
  }

  immediateApi.setSpeed(pUuid, pSpeed).then(function() {
    immediateApi.setAcceleration(pUuid, pAcceleration).then(function() {
      immediateApi.moveRelative(pUuid, pJogIncrement).then(function(pResult) {
        if (pCallback) pCallback(Boolean(pResult));
      });
    });
  });
};

EdgeMachineAppInterface.prototype.stopAll = function() {
  return stopAll(this._getMachineLogicLibrary());
};

module.exports = {
  EdgeMachineAppInterface: EdgeMachineAppInterface
};
